import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { users } from "../utils/api";
import { useToast } from "../contexts/ToastContext";
import { useAdminSettings } from "../contexts/AdminSettingsContext";

const EducationReportStudentsPage = ({ user }) => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const showErrorRef = useRef(showError);
  const { showInactiveStudents } = useAdminSettings();
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  useEffect(() => {
    let active = true;

    const loadStudents = async () => {
      setLoading(true);
      try {
        const includeInactive = user?.role === "admin" && showInactiveStudents;
        const response = await users.getStudents(includeInactive);
        if (active) setStudents(response.data);
      } catch (error) {
        if (active) showErrorRef.current("Failed to fetch students");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadStudents();
    return () => {
      active = false;
    };
  }, [showInactiveStudents, user?.role]);

  const filteredStudents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return students;
    return students.filter((student) =>
      `${student.first_name} ${student.last_name}`
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [searchTerm, students]);

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-10">
      <main className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow p-5 md:p-7">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 border-b pb-5">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Education Report</h1>
              <p className="text-gray-600 mt-1">
                Select a student to view their education report.
              </p>
            </div>
            <div className="w-full md:w-80">
              <label
                htmlFor="education-student-search"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Search students
              </label>
              <input
                id="education-student-search"
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-gray-600">
              Loading students...
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="py-12 text-center text-gray-600">No students found.</div>
          ) : (
            <div className="divide-y">
              {filteredStudents.map((student) => (
                <div
                  key={student.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-4"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {student.first_name} {student.last_name}
                    </p>
                    <p className="text-sm text-gray-500">{student.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/education-report/${student.id}`)}
                    className="self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    View Education Report
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EducationReportStudentsPage;
